import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { withTheme } from '@mui/styles';
import { useMediaQuery } from '@mui/material';

import { StyledTabs, StyledTab, StyledLink } from '@selfserve/react-ui';
import { accountPaths } from '@selfserve/react-utils';

import { StyledAccountLayoutTitle } from '../feature/account-layout';
import { AddPlan } from './add-plan';

const StyledMobileInfoContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    padding: `0 ${theme.spacing(3)}`,
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      padding: `0 ${theme.spacing(2)}`,
      flexDirection: 'column',
      alignItems: 'flex-start',
      button: {
        marginTop: theme.spacing(3),
      },
    },
  })
);
const StyledTabsContainer = styled('div')(({ theme }: { theme?: any }) => ({
  padding: `0 ${theme.spacing(3)}`,
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
}));

const StyledMenuLink = styled(StyledLink, {
  shouldForwardProp: (prop) => {
    return prop !== 'isActive';
  },
})(({ theme, isActive }: { theme?: any; isActive?: boolean }) => ({
  padding: '12px 0',
  position: 'relative',
  color: isActive ? theme.palette.primary.main : theme.palette.grey['300'],
  '&:first-of-type': {
    paddingTop: '6px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: theme.typography.pxToRem(16),
    '&:first-of-type': {
      paddingTop: 0,
    },
  },
}));

const StyledLinkIcon = styled('span', {
  shouldForwardProp: (prop) => {
    return prop !== 'logo' && prop !== 'isActive';
  },
})(
  ({
    theme,
    logo,
    isActive,
  }: {
    theme?: any;
    logo?: string;
    isActive?: boolean;
  }) => ({
    '&::before': {
      position: 'absolute',
      left: '-40px',
      content: '""',
      background: isActive
        ? theme.palette.primary.main
        : theme.palette.grey['300'],
      WebkitMask: `no-repeat url("${logo}") center`,
      width: '24px',
      height: '24px',
    },
  })
);

interface Props {
  theme: any;
  isOverview: boolean;
  currentPath: string;
}

export const AccountNavigation = withTheme(
  ({ theme, currentPath, isOverview }: Props) => {
    const { t } = useTranslation();
    const matchesMd = useMediaQuery(theme.breakpoints.down('md'));

    if (matchesMd) {
      return (
        <>
          <StyledMobileInfoContainer>
            <StyledAccountLayoutTitle isSmall>
              {t('common.account-management')}
            </StyledAccountLayoutTitle>
            {isOverview && <AddPlan />}
          </StyledMobileInfoContainer>
          <StyledTabsContainer>
            <StyledTabs variant="scrollable" value={currentPath}>
              {accountPaths.map(({ name, path, id }) => (
                <StyledTab
                  value={path}
                  key={path}
                  label={
                    <StyledMenuLink id={id} to={path as string} key={path}>
                      {name}
                    </StyledMenuLink>
                  }
                />
              ))}
            </StyledTabs>
          </StyledTabsContainer>
        </>
      );
    }

    return (
      <>
        {accountPaths.map(({ name, path, logo, id }) => {
          const isActive = currentPath === path;
          const links = path?.split('/');
          const navigationItem = links?.[links?.length - 1];
          return (
            <StyledMenuLink id={id} to={path as string} key={path} isActive={isActive}>
              <StyledLinkIcon
                className={`link-icon ${navigationItem}-icon`}
                logo={logo}
                isActive={isActive}
              />
              {name}
            </StyledMenuLink>
          );
        })}
      </>
    );
  }
);
